import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import {
    DEFAULT_PAGE_SIZE_OPTION,
    handlePageClick,
    handlePageSizeChange,
} from "helpers/PaginationHelper";
import {
    CustomTable,
    findUserRoleByLabel,
    Modal,
    ModalHeader,
    OptionType,
    Pagination,
    TableColumn,
    TableType,
    useGetUserAccounts,
    UserAccountActiveStatus,
    UserInfo,
    UserInfoLoading,
    useTitle,
} from "modules/common";
import { AdministrationFilters, deactivateUserByUid, getUserAccountData } from "modules/dashboard";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SortingRule } from "react-table";
import { UserAccounts } from "services";
import { RootState } from "store";

const PAGE_NEIGHBOURS = 1;

export const UserAdministration: React.FC = () => {
    useTitle("Administracija korisnika - eSavjetnik");
    const dispatch = useDispatch();

    const userAccount = useSelector((state: RootState) => state.userAccount);
    const { userData } = userAccount;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [countyFilter, setCountyFilter] = useState<number[]>([]);
    const [roleFilter, setRoleFilter] = useState<number[]>([]);
    const [statusFilter, setStatusFilter] = useState<UserAccountActiveStatus[]>([]);
    const [sortBy, setSortBy] = useState<SortingRule<object>>();
    const [search, setSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);

    const handleModalOpen = useCallback(
        (uid: string, userRole: number) => {
            dispatch(getUserAccountData(uid, userRole));
            setIsModalOpen(true);
        },
        [dispatch]
    );

    const handleModalClose = () => setIsModalOpen(false);

    const handleActivateUser = (uid: string, userRole: number, isDeactivate: boolean) => {
        dispatch(deactivateUserByUid(uid, userRole, isDeactivate));
        setIsModalOpen(false);
        refetchUsers();
    };

    const {
        data: users,
        isFetching: isLoading,
        refetch: refetchUsers,
    } = useGetUserAccounts({
        pageNumber: currentPage,
        pageSize: parseInt(pageSize.value),
        countyFilter: countyFilter,
        registryFilter: [],
        userRoleFilter: roleFilter,
        search: search,
        statusFilter: statusFilter,
        sortField: sortBy?.id,
        descending: sortBy?.desc,
        citiesMunicipalityFilter: [],
        measureFilter: [],
        operationFilter: [],
        entryYearFilter: [],
        isDoneFilter: [],
    });

    const tableColumns = useMemo(() => {
        const columns: TableColumn<UserAccounts>[] = [
            {
                Header: "NAZIV KORISNIKA",
                id: "name",
                accessor: (data: any) => {
                    return (
                        <span>
                            {data.firstName} {data.lastName}
                        </span>
                    );
                },
            },
            {
                Header: "ULOGA",
                accessor: "roleType",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return <div className="role-row ">{original.userRole}</div>;
                },
            },
            {
                Header: "ŽUPANIJA",
                accessor: "county",
            },
            {
                Header: "MIBPG",
                accessor: "mibpg",
            },
            {
                Header: "OIB",
                accessor: "oib",
            },
            {
                Header: "GRAD",
                accessor: "cityMunicipality",
            },
            {
                Header: "EMAIL",
                accessor: "email",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return (
                        <a href={`mailto:${original.email}`} className="email-link-cell">
                            {original.email}
                        </a>
                    );
                },
            },
            {
                Header: "STATUS",
                accessor: "isActive",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return <span>{original.isActive ? "Aktivan" : "Neaktivan"}</span>;
                },
            },
            {
                Header: "DATUM PROMJENE STATUSA",
                accessor: "lastStatusUpdate",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return <span>{getDisplayDateFormat(original.lastStatusUpdate)}</span>;
                },
            },
            {
                Header: "DATUM ZADNJE PRIJAVE",
                accessor: "lastLogin",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return <span>{getDisplayDateFormat(original.lastLogin)}</span>;
                },
            },
            {
                id: "info",
                disableSortBy: true,
                Cell: ({ row }: any) => {
                    const { original } = row;
                    const type = findUserRoleByLabel(original.userRole);
                    return (
                        <DetailsIcon
                            className="details-icon"
                            onClick={() => handleModalOpen(original.uid, type?.value ?? 0)}
                        />
                    );
                },
            },
        ];

        return columns;
    }, [handleModalOpen]);

    return (
        <>
            <div className="user-administration-container">
                <div className="user-administration-content">
                    <div className="f-c g-24 f-1">
                        <AdministrationFilters
                            roleFilter={roleFilter}
                            setRoleFilter={setRoleFilter}
                            countyFilter={countyFilter}
                            setCountyFilter={setCountyFilter}
                            statusFilter={statusFilter}
                            setStatusFilter={setStatusFilter}
                            setCurrentPage={setCurrentPage}
                            searchFilter={search}
                            setSearchFilter={setSearch}
                        />
                        <div className="user-administration-table">
                            <CustomTable
                                data={users?.userAccountList ?? []}
                                columns={tableColumns}
                                setSortBy={setSortBy}
                                tableType={TableType.AdministrationTable}
                                loading={isLoading}
                                flex
                            />

                            {!isLoading && Boolean(users) && users?.userAccountCount !== 0 && (
                                <Pagination
                                    currentPage={currentPage}
                                    numberOfPages={Math.ceil(
                                        (users?.userAccountCount ?? 0) / parseInt(pageSize.value)
                                    )}
                                    pageNeighbours={PAGE_NEIGHBOURS}
                                    pageSize={pageSize}
                                    onPageSizeChange={v =>
                                        handlePageSizeChange(v, setPageSize, setCurrentPage)
                                    }
                                    handlePageClick={e => handlePageClick(e, setCurrentPage)}
                                    handlePageSelect={e => setCurrentPage(e)}
                                    next={() => setCurrentPage(p => p + 1)}
                                    prev={() => setCurrentPage(p => p - 1)}
                                    first={() => setCurrentPage(1)}
                                    last={() =>
                                        setCurrentPage(
                                            Math.ceil(
                                                (users?.userAccountCount ??
                                                    0) / parseInt(pageSize.value)
                                            )
                                        )
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isActive={isModalOpen}
                onClose={handleModalClose}
                modalHeader={
                    <ModalHeader
                        onClose={handleModalClose}
                        title={`${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`}
                        isLoading={userAccount.isFetchingUserData || !userData}
                    />
                }
            >
                {userAccount.isFetchingUserData || !userData ? (
                    <UserInfoLoading />
                ) : (
                    <UserInfo userInfo={userData} onDeactivateClick={handleActivateUser} />
                )}
            </Modal>
        </>
    );
};
